/* eslint-disable react/prop-types */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';

import VendorTab from './VendorTab';
import VendorGeneral from '../tabs/General/VendorGeneral';
import VendorAccounting from '../tabs/Accounting/VendorAccounting';
import vendorService from '../../../services/VendorService';

import { Plugin, Template, TemplateConnector, TemplatePlaceholder } from '@devexpress/dx-react-core';

import { GridExporter } from '@devexpress/dx-react-grid-export';

import {
  PagingState,
  IntegratedPaging,
  SearchState,
  IntegratedFiltering,
  IntegratedSelection,
  SelectionState,
  SortingState,
  IntegratedSorting
} from '@devexpress/dx-react-grid';
import { EditingState } from '@devexpress/dx-react-grid';
import {
  Grid,
  Table,
  TableHeaderRow,
  TableColumnResizing,
  TableEditColumn,
  PagingPanel,
  DragDropProvider,
  TableColumnReordering,
  ColumnChooser,
  TableColumnVisibility,
  Toolbar,
  SearchPanel,
  ExportPanel,
  TableSelection,
  TableInlineCellEditing
} from '@devexpress/dx-react-grid-material-ui';

import Dialog from '@mui/material/Dialog';
import Paper from '@mui/material/Paper';
import { toast } from 'react-hot-toast';
import HeaderFilterCell, {
  getRowCellValue,
  HeaderFilterContext
} from '../../CustomerNew/table/customTemplate/HeaderFilterCell';
import { useLocalStorage } from 'react-use';
import KebabCommandCell from './customTemplate/KebabCommandCell';
import CustomToolbarRoot from '../../UserNew/table/customTemplate/CustomToolbarRoot';

const Popup = ({ row, onChange, onApplyChanges, onCancelChanges, open, vendorTabIndex, setVendorTabIndex }) => {
  return (
    <Dialog open={open} onClose={onCancelChanges} aria-labelledby="form-dialog-title" fullWidth maxWidth="lg">
      {/* Dialog contains tab and tab contents: CustomerTab is tab part */}
      <VendorTab vendorTabIndex={vendorTabIndex} setVendorTabIndex={setVendorTabIndex} />

      {/* General and Accounting is the content of tab  */}
      {vendorTabIndex === 0 && (
        <VendorGeneral
          row={row}
          onChange={onChange}
          onApplyChanges={onApplyChanges}
          onCancelChanges={onCancelChanges}
        />
      )}
      {vendorTabIndex === 1 && (
        <VendorAccounting
          row={row}
          onChange={onChange}
          onCancelChanges={onCancelChanges}
          onApplyChanges={onApplyChanges}
        />
      )}
    </Dialog>
  );
};

// eslint-disable-next-line react/display-name, react/prop-types
const PopupEditing = React.memo(({ popupComponent: Popup, vendorTabIndex, setVendorTabIndex }) => (
  <Plugin>
    <Template name="popupEditing">
      <TemplateConnector>
        {(
          { rows, getRowId, addedRows, editingRowIds, createRowChange, rowChanges },
          {
            changeRow,
            changeAddedRow,
            commitChangedRows,
            commitAddedRows,
            stopEditRows,
            cancelAddedRows,
            cancelChangedRows
          }
        ) => {
          const isNew = addedRows.length > 0;
          let editedRow;
          let rowId;
          if (isNew) {
            rowId = 0;
            editedRow = addedRows[rowId];
          } else {
            [rowId] = editingRowIds;
            const targetRow = rows.filter((row) => getRowId(row) === rowId)[0];
            editedRow = { ...targetRow, ...rowChanges[rowId] };
          }

          const processValueChange = ({ target: { name, value } }) => {
            const changeArgs = {
              rowId,
              change: createRowChange(editedRow, value, name)
            };
            if (isNew) {
              changeAddedRow(changeArgs);
            } else {
              changeRow(changeArgs);
            }
          };
          const rowIds = isNew ? [0] : editingRowIds;
          const applyChanges = () => {
            if (isNew) {
              commitAddedRows({ rowIds });
            } else {
              const changes = rowChanges[rowId];

              if (!changes || Object.keys(changes).length === 0) {
                return toast.error('Please change a field before saving the vendor');
              }

              const updates = [vendorService.updateQboVendor(rowId, changes)];

              // eslint-disable-next-line no-undef
              Promise.all(updates)
                .then(() => {
                  // Commit while the row is still marked as a popup edit so the
                  // grid does not send Verd-only fields through the inline QBO path.
                  commitChangedRows({ rowIds });
                  toast.success('Successfully updated vendor');
                })
                .catch((error) => {
                  const errorMessage = error.response?.data?.message;
                  toast.error(errorMessage || 'Could not update vendor');
                });
            }
          };
          const cancelChanges = () => {
            if (isNew) {
              cancelAddedRows({ rowIds });
            } else {
              stopEditRows({ rowIds });
              cancelChangedRows({ rowIds });
              setVendorTabIndex(0);
            }
          };

          const open = editingRowIds.length > 0 || isNew;
          return (
            <Popup
              open={open}
              row={editedRow}
              onChange={processValueChange}
              onApplyChanges={applyChanges}
              onCancelChanges={cancelChanges}
              vendorTabIndex={vendorTabIndex}
              setVendorTabIndex={setVendorTabIndex}
            />
          );
        }}
      </TemplateConnector>
    </Template>
    <Template name="root">
      <TemplatePlaceholder />
      <TemplatePlaceholder name="popupEditing" />
    </Template>
  </Plugin>
));

const onSave = (workbook) => {
  workbook.xlsx.writeBuffer().then((buffer) => {
    // eslint-disable-next-line no-undef
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
  });
};

const getRowId = (row) => row.id;

const VENDOR_INLINE_COLUMNS = [
  'Email',
  'PrimaryPhone',
  'Line1',
  'City',
  'CountrySubDivisionCode',
  'PostalCode',
  'TaxIdentifier',
  'TermRef',
  'BillRate',
  'BankAccountName',
  'BankBranchIdentifier',
  'BankAccountNumber',
  'StatementText',
  'AcctNum',
  'CostRate',
  'Suffix',
  'GivenName',
  'MiddleName',
  'FamilyName',
  'CompanyName',
  'PrintOnCheckName'
];

// Focusing a data cell starts the same inline editor as a single click.
const VendorFocusableCell = ({ onClick, ...restProps }) => {
  const handleFocus = (event) => {
    if (event.currentTarget !== event.target) return;
    onClick();
  };

  return <Table.Cell {...restProps} tabIndex={0} onFocus={handleFocus} />;
};

// Single-clicking remains available to TableInlineCellEditing. Double-clicking
// a data row switches to the full vendor popup editor.
const ClickableVendorRow = ({ row, onOpen, ...restProps }) => {
  const handleDoubleClick = (event) => {
    if (
      !row ||
      event.target.closest('button, a, input[type="checkbox"], [role="checkbox"], [role="button"], [role="menuitem"]')
    )
      return;
    onOpen(getRowId(row));
  };

  const handleKeyDown = (event) => {
    if (event.currentTarget !== event.target) return;
    if (!row || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onOpen(getRowId(row));
  };

  return (
    <Table.Row
      {...restProps}
      row={row}
      tabIndex={row ? 0 : undefined}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      aria-label={row ? `Open ${row.DisplayName || 'vendor'} details` : undefined}
      style={{
        ...restProps.style,
        cursor: row ? 'pointer' : restProps.style?.cursor
      }}
    />
  );
};

const VendorTable = ({ rows, setRows, selection, setSelection }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [pageSizes] = useState([25, 50, 100]);

  const [vendorTabIndex, setVendorTabIndex] = useState(0);

  const editingModeRef = useRef(null);
  const [editingCellIds, setEditingCellIds] = useState([]);
  const [editingRowIds, setEditingRowIds] = useState([]);

  const changeEditingMode = useCallback((mode) => {
    editingModeRef.current = mode;
  }, []);

  const startPopupEditing = useCallback(() => {
    setEditingCellIds([]);
    changeEditingMode('popup');
    setVendorTabIndex(0);
  }, [changeEditingMode]);

  const openVendorDetails = useCallback(
    (rowId) => {
      setEditingCellIds([]);
      changeEditingMode('popup');
      setVendorTabIndex(0);
      setEditingRowIds([rowId]);
    },
    [changeEditingMode]
  );

  const [columns] = useState([
    { name: 'DisplayName', title: 'DisplayName' },
    { name: 'Email', title: 'Email' },
    { name: 'PrimaryPhone', title: 'PrimaryPhone' },
    { name: 'vendorStatus', title: 'Action' },
    { name: 'VendorId', title: 'VendorId' },

    { name: 'BillAddrId', title: 'BillAddrId' },
    { name: 'Line1', title: 'Line1' },
    { name: 'City', title: 'City' },
    { name: 'CountrySubDivisionCode', title: 'CountrySubDivisionCode' },
    { name: 'PostalCode', title: 'PostalCode' },

    { name: 'TaxIdentifier', title: 'TaxIdentifier' },
    { name: 'TermRef', title: 'TermRef' },

    { name: 'Balance', title: 'Balance' },
    { name: 'BillRate', title: 'BillRate' },
    { name: 'Vendor1099', title: 'Vendor1099' },

    { name: 'BankAccountName', title: 'BankAccountName' },
    { name: 'BankBranchIdentifier', title: 'BankBranchIdentifier' },
    { name: 'BankAccountNumber', title: 'BankAccountNumber' },
    { name: 'StatementText', title: 'StatementText' },

    { name: 'AcctNum', title: 'AcctNum' },
    { name: 'CostRate', title: 'CostRate' },
    { name: 'sparse', title: 'sparse' },
    { name: 'SyncToken', title: 'SyncToken' },

    { name: 'Suffix', title: 'Suffix' },
    { name: 'Active', title: 'Active' },
    { name: 'V4IDPseudonym', title: 'V4IDPseudonym' },

    { name: 'GivenName', title: 'GivenName' },
    { name: 'MiddleName', title: 'MiddleName' },
    { name: 'FamilyName', title: 'FamilyName' },
    { name: 'CompanyName', title: 'CompanyName' },
    { name: 'PrintOnCheckName', title: 'PrintOnCheckName' }
  ]);

  const [vendorColumnWidths, setVendorColumnWidths] = useState([
    { columnName: 'DisplayName', width: 180 },
    { columnName: 'Email', width: 180 },
    { columnName: 'PrimaryPhone', width: 180 },
    { columnName: 'vendorStatus', width: 180 },
    { columnName: 'VendorId', width: 180 },

    { columnName: 'BillAddrId', width: 180 },
    { columnName: 'Line1', width: 180 },
    { columnName: 'City', width: 180 },
    { columnName: 'CountrySubDivisionCode', width: 180 },
    { columnName: 'PostalCode', width: 180 },

    { columnName: 'TaxIdentifier', width: 180 },
    { columnName: 'TermRef', width: 180 },
    { columnName: 'Balance', width: 180 },
    { columnName: 'BillRate', width: 180 },
    { columnName: 'Vendor1099', width: 180 },

    { columnName: 'BankAccountName', width: 180 },
    { columnName: 'BankBranchIdentifier', width: 180 },
    { columnName: 'BankAccountNumber', width: 180 },
    { columnName: 'StatementText', width: 180 },

    { columnName: 'AcctNum', width: 180 },
    { columnName: 'CostRate', width: 180 },
    { columnName: 'sparse', width: 180 },
    { columnName: 'SyncToken', width: 180 },

    { columnName: 'Suffix', width: 180 },
    { columnName: 'Active', width: 180 },
    { columnName: 'V4IDPseudonym', width: 180 },

    { columnName: 'GivenName', width: 180 },
    { columnName: 'MiddleName', width: 180 },
    { columnName: 'FamilyName', width: 180 },
    { columnName: 'CompanyName', width: 180 },
    { columnName: 'PrintOnCheckName', width: 180 }
  ]);
  const [vendorColumnOrder, setVendorColumnOrder] = useState([
    'DisplayName',
    'Email',
    'PrimaryPhone',
    'vendorStatus',
    'VendorId',
    'BillAddrId',
    'Line1',
    'City',
    'CountrySubDivisionCode',
    'PostalCode',
    'TaxIdentifier',
    'TermRef',
    'Balance',
    'BillRate',
    'Vendor1099',
    'BankAccountName',
    'BankBranchIdentifier',
    'BankAccountNumber',
    'StatementText',
    'AcctNum',
    'CostRate',
    'sparse',
    'SyncToken',
    'Suffix',
    'Active',
    'V4IDPseudonym',
    'GivenName',
    'MiddleName',
    'FamilyName',
    'CompanyName',
    'PrintOnCheckName'
  ]);

  const [hiddenColumnNames, setHiddenColumnNames] = useState(['']);
  const [searchValue, setSearchState] = useState('');
  const [sorting, setSorting] = useState([{ columnName: 'DisplayName', direction: 'asc' }]);

  const [columnFilters, setColumnFilters] = useLocalStorage('vendorColumnFilters', {});

  const editingStateColumnExtensions = useMemo(
    () => columns.map(({ name }) => ({ columnName: name, editingEnabled: VENDOR_INLINE_COLUMNS.includes(name) })),
    [columns]
  );

  const exporterRef = useRef(null);

  const startExport = useCallback(
    (options) => {
      exporterRef.current.exportGrid(options);
    },
    [exporterRef]
  );

  const commitChanges = ({ changed }) => {
    const validChanges = Object.fromEntries(
      Object.entries(changed || {}).filter(
        ([, rowChanges]) => rowChanges && typeof rowChanges === 'object' && Object.keys(rowChanges).length > 0
      )
    );

    if (Object.keys(validChanges).length === 0) return;

    const changedRows = rows.map((row) => (validChanges[row.id] ? { ...row, ...validChanges[row.id] } : row));

    // PopupEditing sends its own field-only request. Only inline commits are sent here.
    const isPopupCommit = editingRowIds.length > 0 || editingModeRef.current === 'popup';
    if (!isPopupCommit) {
      const updates = Object.keys(validChanges)
        .map((rowId) => {
          const qboChanges = validChanges[rowId];
          if (Object.keys(qboChanges).length === 0) return null;

          const vendor = rows.find((row) => String(row.id) === String(rowId));
          if (!vendor) throw new Error(`Vendor row ${rowId} was not found`);
          return vendorService.updateQboVendor(vendor.id, qboChanges);
        })
        .filter(Boolean);

      if (updates.length > 0) {
        // eslint-disable-next-line no-undef
        Promise.all(updates)
          .then(() => toast.success('Successfully updated vendor in quickbook'))
          .catch((error) => {
            setRows(rows);
            const message = error?.response?.data?.message || error?.message || 'Could not update vendor in quickbook';
            toast.error(message);
          });
      }
    }

    setRows(changedRows);
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      // Column header filters
      for (const columnName of Object.keys(columnFilters)) {
        const activeVals = columnFilters[columnName];
        if (activeVals && activeVals.length > 0) {
          const rowVal = getRowCellValue(row, columnName);
          if (!activeVals.includes(rowVal)) {
            return false;
          }
        }
      }

      return true;
    });
  }, [rows, columnFilters]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div>Total rows selected: {selection.length}</div>
      </div>

      <HeaderFilterContext.Provider value={{ rows, columnFilters, onFilterChange: setColumnFilters }}>
        <Paper>
          <Grid rows={filteredRows} columns={columns} getRowId={getRowId}>
            <SortingState sorting={sorting} onSortingChange={setSorting} />
            <PagingState
              currentPage={currentPage}
              onCurrentPageChange={setCurrentPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />

            <SelectionState selection={selection} onSelectionChange={setSelection} />
            <EditingState
              editingCellIds={editingCellIds}
              onEditingCellIdsChange={(cellIds) => {
                if (editingModeRef.current !== 'popup' || editingModeRef.current === null) {
                  const editableCellIds = cellIds.filter(({ columnName }) =>
                    VENDOR_INLINE_COLUMNS.includes(columnName)
                  );
                  changeEditingMode(editableCellIds.length ? 'inline' : null);
                  setEditingCellIds(editableCellIds);
                }
              }}
              editingRowIds={editingRowIds}
              onEditingRowIdsChange={(rowIds) => {
                setEditingRowIds(rowIds);
                if (rowIds.length) {
                  setEditingCellIds([]);
                  changeEditingMode('popup');
                } else if (editingModeRef.current === 'popup') {
                  changeEditingMode(null);
                }
              }}
              onCommitChanges={commitChanges}
              columnExtensions={editingStateColumnExtensions}
            />

            <IntegratedSorting />
            <IntegratedSelection />

            <DragDropProvider />

            <SearchState value={searchValue} onValueChange={setSearchState} />
            <IntegratedFiltering />
            <IntegratedPaging />

            <Table
              rowComponent={(props) => <ClickableVendorRow {...props} onOpen={openVendorDetails} />}
              cellComponent={VendorFocusableCell}
            />

            <TableColumnResizing columnWidths={vendorColumnWidths} onColumnWidthsChange={setVendorColumnWidths} />
            <TableColumnReordering order={vendorColumnOrder} onOrderChange={setVendorColumnOrder} />

            <TableHeaderRow showSortingControls cellComponent={HeaderFilterCell} />
            <TableSelection showSelectAll />

            {/* Native grid edit commands, displayed through the shared KebabCommandCell. */}
            <TableEditColumn
              width={90}
              showEditCommand
              cellComponent={(props) => <KebabCommandCell {...props} onPopupEditStart={startPopupEditing} />}
            />

            <TableInlineCellEditing startEditAction="click" selectTextOnEditStart={true} />

            <PopupEditing
              popupComponent={Popup}
              vendorTabIndex={vendorTabIndex}
              setVendorTabIndex={setVendorTabIndex}
            />

            <TableColumnVisibility
              hiddenColumnNames={hiddenColumnNames}
              onHiddenColumnNamesChange={setHiddenColumnNames}
            />

            {/* Pass the custom root to the Toolbar */}
            <Toolbar rootComponent={CustomToolbarRoot} />

            <SearchPanel />
            <ColumnChooser />
            <PagingPanel pageSizes={pageSizes} />
            <ExportPanel startExport={startExport} />
          </Grid>
          <GridExporter
            ref={exporterRef}
            rows={filteredRows}
            columns={columns}
            hiddenColumnNames={hiddenColumnNames}
            selection={selection}
            onSave={onSave}
            getRowId={getRowId}
          />
        </Paper>
      </HeaderFilterContext.Provider>
    </div>
  );
};

export default VendorTable;

VendorTable.propTypes = {
  rows: PropTypes.any,
  setRows: PropTypes.func,
  selection: PropTypes.array,
  setSelection: PropTypes.func
};
